import { useCallback } from 'react';

import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';

import { useMobileSignupNavContext } from '../../hooks/useMobileSignupNavContext';
import { TMobileSignupForm } from '../form';

type TFieldName = keyof TMobileSignupForm;

// Validates the whole-flow form store against one screen's schema on CTA tap.
// The provider store has no resolver (it spans every screen), so each screen
// validates its own subset here rather than owning a second useForm.
export const useScreenValidation = (
  schema: yup.AnyObjectSchema,
  onValid?: () => void,
) => {
  const { getValues, setError, clearErrors, setFocus } =
    useFormContext<TMobileSignupForm>();
  const { goNext } = useMobileSignupNavContext();

  const validateAndContinue = useCallback(async () => {
    clearErrors();
    try {
      await schema.validate(getValues(), { abortEarly: false });
      (onValid ?? goNext)();
    } catch (error) {
      if (!(error instanceof yup.ValidationError)) throw error;

      error.inner.forEach((fieldError) => {
        if (!fieldError.path) return;
        setError(fieldError.path as TFieldName, {
          type: fieldError.type ?? 'validate',
          message: fieldError.message,
        });
      });

      const firstPath = error.inner.find((entry) => entry.path)?.path;
      if (firstPath) setFocus(firstPath as TFieldName);
    }
  }, [schema, getValues, setError, clearErrors, setFocus, goNext, onValid]);

  return { validateAndContinue };
};
